import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { BadgeCheck, Star, MapPin, Globe, MessageSquare, MessageCircle, Plus, Check, ShoppingBag } from "lucide-react";
import { useI18n } from "../i18n/I18nContext";
import { useAuth } from "../context/AuthContext";
import api, { fileUrl } from "../lib/api";
import { useSeo } from "../lib/useSeo";
import { formatDate } from "../lib/format";
import { ISLAND_NAME } from "../lib/data";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Button } from "../components/ui/button";
import { ListingCard } from "../components/ListingCard";
import ReviewsModal from "../components/ReviewsModal";

export default function UserProfile() {
  const { id } = useParams();
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [listings, setListings] = useState([]);
  const [following, setFollowing] = useState(false);
  const [reviewsOpen, setReviewsOpen] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get(`/users/${id}`);
    setProfile(data);
    setFollowing(user && data.followers?.includes(user.id));
    const ls = await api.get("/listings", { params: { seller_id: id } });
    setListings(ls.data);
  }, [id, user]);
  useEffect(() => { load(); }, [load]);
  // Real-time: a new review from the counterparty updates rating/average without manual reload.
  useEffect(() => {
    const iv = setInterval(() => { api.get(`/users/${id}`).then(({ data }) => setProfile(data)).catch(() => {}); }, 12000);
    return () => clearInterval(iv);
  }, [id]);

  const seoName = profile ? (profile.nickname ? `@${profile.nickname}` : (profile.is_professional ? profile.business_name || profile.name : profile.name)) : "";
  const seoFallback = lang === "it" ? `Scopri gli annunci e le attività di ${seoName} su Canarias Market`
    : lang === "en" ? `Discover the listings and services of ${seoName} on Canarias Market`
    : `Descubre los anuncios y actividades de ${seoName} en Canarias Market`;
  const seoPhoto = profile ? (profile.is_professional ? profile.business_logo || profile.profile_photo : profile.profile_photo) : null;
  useSeo({
    title: profile ? `${seoName} - Canarias Market` : "Canarias Market",
    description: profile ? (profile.bio || seoFallback) : "",
    image: seoPhoto ? fileUrl(seoPhoto) : undefined,
    type: "profile",
  });

  if (!profile) return <div className="p-10 text-center text-slate-400">{t("common.loading")}</div>;
  const isOwn = user && user.id === id;

  const follow = async () => {
    if (!user) { navigate("/auth"); return; }
    const { data } = await api.post(`/users/${id}/follow`); setFollowing(data.following);
  };
  const contact = async () => {
    if (!user) { navigate("/auth"); return; }
    await api.post("/messages", { receiver_id: id, text: `Hola ${profile.name} 👋` });
    navigate("/messages");
  };

  return (
    <div className="px-4 sm:px-8 py-6">
      <div className="bg-white border border-slate-200 rounded-3xl p-6 mb-6">
        <div className="flex items-start gap-4">
          <Avatar className="h-20 w-20"><AvatarImage src={fileUrl(profile.is_professional ? profile.business_logo : profile.profile_photo)} /><AvatarFallback className="bg-teal-100 text-teal-700 text-2xl">{profile.name?.[0]}</AvatarFallback></Avatar>
          <div className="flex-1">
            <h1 className="font-display font-extrabold text-2xl flex items-center gap-2">{profile.is_professional ? profile.business_name || profile.name : profile.name}{profile.is_professional && <BadgeCheck className="h-5 w-5 text-[#008080]" />}</h1>
            {profile.nickname && <p className="text-slate-400 text-sm">@{profile.nickname}</p>}
            <p className="text-sm text-slate-500 flex items-center gap-1 mt-1"><MapPin className="h-3.5 w-3.5" />{profile.municipality || "—"}{profile.island ? `, ${ISLAND_NAME[profile.island]}` : ""}</p>
            {profile.bio && <p className="text-sm text-slate-600 mt-2">{profile.bio}</p>}
            {profile.website && <a href={profile.website} target="_blank" rel="noreferrer" className="text-sm text-[#008080] flex items-center gap-1 mt-1"><Globe className="h-3.5 w-3.5" />{profile.website}</a>}
            <div className="flex items-center gap-4 mt-3 text-sm">
              <button data-testid="profile-rating-btn" onClick={() => setReviewsOpen(true)} className="flex items-center gap-1 hover:text-amber-600 transition-colors cursor-pointer"><Star className="h-4 w-4 fill-amber-400 text-amber-400" />{profile.rating || 0} <span className="text-slate-400">({profile.review_count})</span></button>
              <span><b>{profile.listing_count}</b> <span className="text-slate-400">{t("profile.listings")}</span></span>
              <span data-testid="profile-sold-count" className="flex items-center gap-1"><ShoppingBag className="h-4 w-4 text-[#008080]" /><b>{profile.sold_count || 0}</b> <span className="text-slate-400">{t("profile.sold")}</span></span>
              <span><b>{profile.followers?.length || 0}</b> <span className="text-slate-400">{t("profile.followers")}</span></span>
            </div>
          </div>
        </div>
        {!isOwn && (
          <div className="flex gap-3 mt-5">
            <Button data-testid="follow-btn" onClick={follow} className={following ? "bg-slate-200 text-slate-700 hover:bg-slate-300" : "bg-[#008080] hover:bg-[#006666]"}>
              {following ? <><Check className="h-4 w-4 mr-1" />{t("common.following")}</> : <><Plus className="h-4 w-4 mr-1" />{t("common.follow")}</>}
            </Button>
            <Button data-testid="contact-btn" onClick={contact} variant="outline"><MessageSquare className="h-4 w-4 mr-1" />{t("common.contact")}</Button>
            {profile.whatsapp_number && (
              <Button data-testid="profile-whatsapp-btn" onClick={() => { const n=(profile.whatsapp_number||"").replace(/[^0-9]/g,""); window.open(`https://wa.me/${n}`, "_blank"); }} className="bg-emerald-500 hover:bg-emerald-600 text-white"><MessageCircle className="h-4 w-4 mr-1" />WhatsApp</Button>
            )}
          </div>
        )}
      </div>

      {profile.reviews?.length > 0 && (
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display font-bold text-lg">{t("listing.reviews")}</h2>
            <button data-testid="reviews-see-all" onClick={() => setReviewsOpen(true)} className="text-sm font-medium text-[#008080] hover:underline">{t("reviewsModal.seeAll")}</button>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {profile.reviews.slice(0, 4).map((r) => (
              <button key={r.id} onClick={() => setReviewsOpen(true)} className="text-left bg-white border border-slate-200 rounded-2xl p-4 hover:border-[#008080]/40 transition-colors">
                <div className="flex items-center gap-2"><span className="flex">{Array.from({ length: 5 }).map((_, i) => <Star key={i} className={`h-3.5 w-3.5 ${i < r.rating ? "fill-amber-400 text-amber-400" : "text-slate-200"}`} />)}</span><span className="text-xs text-slate-500">{r.reviewer_name}</span></div>
                {r.comment && <p className="text-sm text-slate-600 mt-1 line-clamp-2">{r.comment}</p>}
              </button>
            ))}
          </div>
        </div>
      )}

      <h2 className="font-display font-bold text-lg mb-3">{t("profile.listings")}</h2>
      {listings.length === 0 ? <p className="text-slate-400 py-8">{t("profile.noListings")}</p> : (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">{listings.map((l, i) => <ListingCard key={l.id} listing={l} index={i} />)}</div>
      )}

      <ReviewsModal open={reviewsOpen} onOpenChange={setReviewsOpen} userId={id}
        name={profile.is_professional ? profile.business_name || profile.name : profile.name}
        rating={profile.rating} count={profile.review_count} />
    </div>
  );
}
